import type { FC, ReactNode } from 'react';
import { Link } from 'react-router';

import { copyrightStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import {
  BlogLinks,
  MenuLink,
  PageLinks,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  FOOTER_CATEGORY_LIMIT,
  categoryColumns,
  menuItems,
  type FooterProps,
} from '@/features/storefront/layouts/chrome/types';
import { STOREFRONT_MONO_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';

/**
 * Voltic's footer grid, by how many link columns stand beside the wordmark.
 * Written out for the same reason Serra's is: Tailwind reads these class names
 * out of the source and never sees a string built at runtime.
 */
const VOLTIC_FOOTER_GRID: Record<number, string> = {
  0: '',
  1: 'lg:grid-cols-[1.4fr_1fr]',
  2: 'lg:grid-cols-[1.4fr_1fr_1fr]',
  3: 'lg:grid-cols-[1.4fr_1fr_1fr_1fr]',
  4: 'lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]',
};

/**
 * One Voltic footer column: a plain, solid heading over its links.
 *
 * Not Serra's tracked small caps - this family spends its caps on machine
 * details and keeps its headings speaking normally, which is the difference
 * between a house's colophon and a shop's directory.
 */
function VolticColumn({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div>
      <p
        className="mb-3.5 text-[12.5px] font-medium"
        style={{ color: 'var(--storefront-text)' }}
        aria-hidden={title ? undefined : true}
      >
        {title ?? ' '}
      </p>
      {children}
    </div>
  );
}

/**
 * The marketplace directory: a wordmark and what the shop is, over columns of
 * everywhere else it goes, closed by a hairline and the fine print.
 *
 * Same column arithmetic as Serra's colophon - the merchant's footer menu, the
 * shop's own shelves, its pages and blogs, each skipped when empty and the
 * grid sized to what is left - because the alternative is a directory of
 * headings with nothing under them. What differs is the voice: solid headings,
 * denser rows, and small print set in the family's machine face.
 */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const shelfColumns = categoryColumns(
    useShelfCategories({ limit: FOOTER_CATEGORY_LIMIT, enabled: !preview }),
  );
  const categoryPath = useCategoryPath();
  const copy = useShopContent();
  const columns = (items.length > 0 ? 1 : 0) + shelfColumns.length + (preview ? 0 : 1);
  // The merchant may well have called their own menu "Shop" already, and two
  // columns under one heading read as a single list broken in half. Compared
  // against the shop's own word for the column, in the shopper's language.
  const shopTitle = copy('theme.footer_shop');
  const shelvesTitle =
    menu?.name.trim().toLowerCase() === shopTitle.trim().toLowerCase()
      ? copy('header.menu_categories')
      : shopTitle;

  return (
    <footer
      className="border-t pt-11 text-[12.5px]"
      style={{ borderColor: 'var(--storefront-surface)', color: 'var(--storefront-muted)' }}
    >
      <StorefrontContainer
        className={cn('grid gap-9 pb-9 text-start sm:grid-cols-2', VOLTIC_FOOTER_GRID[columns])}
      >
        <div>
          <p
            className="text-xl font-bold tracking-[-0.02em]"
            style={{
              color: 'var(--storefront-text)',
              fontFamily: 'var(--storefront-heading-font)',
            }}
          >
            {storeName}
            <span aria-hidden style={{ color: 'var(--storefront-accent)' }}>
              .
            </span>
          </p>
          {description ? <p className="mt-3 max-w-[19rem] leading-relaxed">{description}</p> : null}
        </div>
        {items.length > 0 ? (
          <VolticColumn title={menu?.name ?? shopTitle}>
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-2.5">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="hover:opacity-60"
                />
              ))}
            </nav>
          </VolticColumn>
        ) : null}
        {shelfColumns.map((column, index) => (
          <VolticColumn key={column[0].id} title={index === 0 ? shelvesTitle : undefined}>
            <nav
              aria-label={
                index === 0 ? shelvesTitle : copy('theme.footer_continued', { title: shelvesTitle })
              }
              className="flex flex-col items-start gap-2.5"
            >
              {column.map((category) => (
                <Link key={category.id} to={categoryPath(category.slug)} className="hover:opacity-60">
                  {category.name}
                </Link>
              ))}
            </nav>
          </VolticColumn>
        ))}
        {preview ? null : (
          <VolticColumn title={copy('theme.footer_support')}>
            <PageLinks className="mb-2 flex-col items-start justify-start gap-2.5" />
            <BlogLinks className="flex-col items-start justify-start gap-2.5" />
            <InstallAppSlot className="mt-4" />
            <AppStoreBadges footer={footer} preview={preview} className="mt-4" />
          </VolticColumn>
        )}
      </StorefrontContainer>
      <div className="border-t" style={{ borderColor: 'var(--storefront-surface)' }}>
        <StorefrontContainer className="flex flex-wrap justify-between gap-x-6 gap-y-2 py-4 text-[11px]">
          <LanguageSwitcherSlot placement="footer" preview={preview} />
          {/* The machine line at the bottom of a marketplace: currency, tax
              treatment, the year. Set in the family's mono face, which is the
              same face its prices are set in a few inches above. */}
          <span style={STOREFRONT_MONO_STYLE}>{copy('theme.prices_inr_gst')}</span>
          <span style={{ ...STOREFRONT_MONO_STYLE, ...copyrightStyle(footer) }}>
            © {new Date().getFullYear()} {storeName} ·{' '}
            {copy('theme.powered_by', { brand: 'NeonCarts' })}
          </span>
        </StorefrontContainer>
      </div>
    </footer>
  );
};
